import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Trophy, Music, ThumbsUp, Clock, Loader2, Send, Play, Crown } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { useAuth } from "../App";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContestsPage = () => {
  const { user } = useAuth();
  const [contests, setContests] = useState([]);
  const [selectedContest, setSelectedContest] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEntry, setShowEntry] = useState(false);
  const [entryForm, setEntryForm] = useState({ beat_title: "", audio_url: "", description: "" });
  const [submitting, setSubmitting] = useState(false);
  const [tab, setTab] = useState("contests");

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [contestsRes, lbRes] = await Promise.all([
          axios.get(`${API}/contests/`),
          axios.get(`${API}/leaderboard`)
        ]);
        setContests(contestsRes.data || []);
        setLeaderboard(lbRes.data || []);
      } catch (e) {} finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const loadContest = async (contest) => {
    try {
      const res = await axios.get(`${API}/contests/${contest.contest_id}`);
      setSelectedContest(res.data);
    } catch (e) { setSelectedContest(contest); }
  };

  const handleEnterContest = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Please sign in"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contests/${selectedContest.contest_id}/enter`, entryForm, { headers: getAuthHeaders(), withCredentials: true });
      toast.success("Entry submitted!");
      setShowEntry(false);
      setEntryForm({ beat_title: "", audio_url: "", description: "" });
      loadContest(selectedContest);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to enter"); }
    finally { setSubmitting(false); }
  };

  const handleVote = async (entryId) => {
    if (!user) { toast.error("Please sign in to vote"); return; }
    try {
      const res = await axios.post(`${API}/contests/entries/${entryId}/vote`, {}, { headers: getAuthHeaders(), withCredentials: true });
      toast.success(res.data.voted ? "Voted!" : "Vote removed");
      if (selectedContest) loadContest(selectedContest);
    } catch (e) { toast.error("Failed to vote"); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-neon-cyan" />
    </div>
  );

  return (
    <div className="min-h-screen py-10 px-6" data-testid="contests-page">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-10">
          <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/50 mb-4">
            <Trophy className="w-3 h-3 mr-1" />Compete
          </Badge>
          <h1 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mb-4">
            Beat Battles & Leaderboard
          </h1>
          <p className="text-zinc-400 max-w-xl mx-auto">
            Show off your production skills, compete with other producers, and climb the ranks.
          </p>
        </div>

        {/* Tab buttons */}
        <div className="flex gap-3 mb-8 justify-center">
          <Button
            variant={tab === "contests" ? "default" : "outline"}
            onClick={() => setTab("contests")}
            className={tab === "contests" ? "bg-neon-cyan text-black" : "border-white/10"}
            data-testid="tab-contests"
          >
            <Trophy className="w-4 h-4 mr-2" />Contests
          </Button>
          <Button
            variant={tab === "leaderboard" ? "default" : "outline"}
            onClick={() => setTab("leaderboard")}
            className={tab === "leaderboard" ? "bg-amber-500 text-black" : "border-white/10"}
            data-testid="tab-leaderboard"
          >
            <Crown className="w-4 h-4 mr-2" />Leaderboard
          </Button>
        </div>

        {tab === "contests" && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Contest List */}
            <div className="space-y-3">
              {contests.length === 0 ? (
                <div className="glass p-8 text-center">
                  <Trophy className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
                  <p className="text-zinc-500">No active contests</p>
                </div>
              ) : contests.map((contest) => (
                <button
                  key={contest.contest_id}
                  onClick={() => loadContest(contest)}
                  className={`w-full text-left glass p-4 transition-colors hover:bg-white/5 ${selectedContest?.contest_id === contest.contest_id ? 'border border-amber-500/50' : ''}`}
                  data-testid={`contest-item-${contest.contest_id}`}
                >
                  <p className="font-medium mb-1">{contest.title}</p>
                  <div className="flex items-center gap-2 text-xs text-zinc-500">
                    <Badge className={contest.status === 'open' ? 'bg-neon-green/20 text-neon-green text-[10px]' : 'bg-red-500/20 text-red-400 text-[10px]'}>
                      {contest.status}
                    </Badge>
                    <span><Clock className="w-3 h-3 inline" /> {new Date(contest.deadline).toLocaleDateString()}</span>
                  </div>
                </button>
              ))}
            </div>

            {/* Contest Detail */}
            <div className="md:col-span-2">
              {selectedContest ? (
                <div className="glass p-6" data-testid="contest-detail">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h2 className="font-heading text-xl">{selectedContest.title}</h2>
                      <p className="text-xs text-zinc-500">Deadline: {new Date(selectedContest.deadline).toLocaleString()}</p>
                    </div>
                    <Badge className={selectedContest.status === 'open' ? 'bg-neon-green/20 text-neon-green' : 'bg-red-500/20 text-red-400'}>
                      {selectedContest.status}
                    </Badge>
                  </div>
                  <p className="text-sm text-zinc-300 mb-3">{selectedContest.description}</p>
                  <div className="glass bg-amber-500/5 border border-amber-500/20 p-4 mb-6">
                    <p className="text-xs text-amber-400 uppercase tracking-wide mb-1">Prize</p>
                    <p className="text-sm text-zinc-200">{selectedContest.prize}</p>
                  </div>

                  {selectedContest.status === "open" && user && (
                    <Dialog open={showEntry} onOpenChange={setShowEntry}>
                      <DialogTrigger asChild>
                        <Button className="bg-amber-500 text-black font-bold mb-6" data-testid="enter-contest-button">
                          <Music className="w-4 h-4 mr-2" />Enter Contest
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-zinc-900 border-white/10 max-w-lg">
                        <DialogHeader><DialogTitle className="font-heading">Submit Your Entry</DialogTitle></DialogHeader>
                        <form onSubmit={handleEnterContest} className="space-y-4">
                          <div>
                            <Label>Beat Title</Label>
                            <Input value={entryForm.beat_title} onChange={(e) => setEntryForm({ ...entryForm, beat_title: e.target.value })} className="bg-black/50 border-white/10" required data-testid="entry-title" />
                          </div>
                          <div>
                            <Label>Audio URL</Label>
                            <Input value={entryForm.audio_url} onChange={(e) => setEntryForm({ ...entryForm, audio_url: e.target.value })} className="bg-black/50 border-white/10" required placeholder="https://..." data-testid="entry-audio-url" />
                          </div>
                          <div>
                            <Label>Description</Label>
                            <Textarea value={entryForm.description} onChange={(e) => setEntryForm({ ...entryForm, description: e.target.value })} className="bg-black/50 border-white/10" rows={3} data-testid="entry-description" />
                          </div>
                          <Button type="submit" disabled={submitting} className="w-full bg-amber-500 text-black font-bold" data-testid="submit-entry-button">
                            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}Submit Entry
                          </Button>
                        </form>
                      </DialogContent>
                    </Dialog>
                  )}

                  {/* Entries */}
                  <h3 className="text-sm uppercase text-zinc-400 mb-3">
                    Entries ({selectedContest.entries?.length || 0})
                  </h3>
                  {selectedContest.entries?.length === 0 ? (
                    <p className="text-zinc-600 text-sm">No entries yet. Be the first!</p>
                  ) : (
                    <div className="space-y-3">
                      {selectedContest.entries?.map((entry, idx) => (
                        <motion.div
                          key={entry.entry_id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: idx * 0.05 }}
                          className={`glass p-4 flex items-center gap-4 ${idx === 0 ? 'border border-amber-500/30' : ''}`}
                          data-testid={`entry-${entry.entry_id}`}
                        >
                          <span className={`font-heading text-xl w-8 text-center ${idx === 0 ? 'text-amber-400' : idx === 1 ? 'text-zinc-300' : idx === 2 ? 'text-orange-600' : 'text-zinc-600'}`}>
                            #{idx + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{entry.beat_title}</p>
                            <p className="text-xs text-zinc-500">by {entry.user_name}</p>
                          </div>
                          <div className="flex items-center gap-3">
                            {entry.audio_url && (
                              <a href={entry.audio_url} target="_blank" rel="noreferrer">
                                <Button variant="ghost" size="icon" className="text-neon-cyan hover:text-neon-cyan/80">
                                  <Play className="w-4 h-4" />
                                </Button>
                              </a>
                            )}
                            <Button
                              variant="ghost"
                              onClick={() => handleVote(entry.entry_id)}
                              className="flex items-center gap-1 text-sm text-zinc-400 hover:text-amber-400"
                              data-testid={`vote-${entry.entry_id}`}
                            >
                              <ThumbsUp className="w-4 h-4" />
                              <span className="font-mono">{entry.votes}</span>
                            </Button>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="glass p-12 text-center">
                  <Trophy className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                  <p className="text-zinc-500">Select a contest to view details</p>
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "leaderboard" && (
          <div className="max-w-2xl mx-auto">
            <div className="glass p-6">
              <h2 className="font-heading text-lg uppercase mb-6 flex items-center gap-2">
                <Crown className="w-5 h-5 text-amber-400" />
                Top Producers
              </h2>
              {leaderboard.length === 0 ? (
                <p className="text-zinc-500 text-center py-8">No producers ranked yet</p>
              ) : (
                <div className="space-y-2">
                  {leaderboard.map((producer, idx) => (
                    <motion.div
                      key={producer.producer}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      className={`flex items-center gap-4 p-4 ${idx < 3 ? 'glass border' : 'bg-white/5 rounded-sm'} ${idx === 0 ? 'border-amber-400/50' : idx === 1 ? 'border-zinc-300/30' : idx === 2 ? 'border-orange-600/30' : ''}`}
                      data-testid={`leaderboard-${idx}`}
                    >
                      <span className={`font-heading text-2xl w-10 text-center ${idx === 0 ? 'text-amber-400' : idx === 1 ? 'text-zinc-300' : idx === 2 ? 'text-orange-600' : 'text-zinc-600'}`}>
                        {idx === 0 ? <Crown className="w-6 h-6 mx-auto text-amber-400" /> : `#${idx + 1}`}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium">{producer.producer}</p>
                        <p className="text-xs text-zinc-500">{producer.beats} beats</p>
                      </div>
                      <div className="flex items-center gap-6 text-xs">
                        <div className="text-center">
                          <p className="font-heading text-lg text-neon-cyan">{producer.plays?.toLocaleString()}</p>
                          <p className="text-zinc-600">Plays</p>
                        </div>
                        <div className="text-center">
                          <p className="font-heading text-lg text-red-400">{producer.likes}</p>
                          <p className="text-zinc-600">Likes</p>
                        </div>
                        <div className="text-center">
                          <p className="font-heading text-lg text-amber-400">{producer.score?.toLocaleString()}</p>
                          <p className="text-zinc-600">Score</p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContestsPage;
